// components/admin/accounting/PaymentsAccounting.jsx - WITH DRIVERS TAB
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Package, Truck, RefreshCw } from 'lucide-react';
import PaymentsTable from './PaymentsTable';

const API_URL = process.env.REACT_APP_API_URL;

function PaymentsAccounting({ userId, selectedDate }) {
  const [activeSubTab, setActiveSubTab] = useState('users');
  const [usersData, setUsersData] = useState([]);
  const [providersData, setProvidersData] = useState([]);
  const [driversData, setDriversData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentsData();
  }, [selectedDate]);

  async function fetchPaymentsData() {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/accounting/payments`, {
        params: {
          admin_id: userId,
          date: selectedDate
        }
      });

      setUsersData(response.data.users || []);
      setProvidersData(response.data.providers || []);
      setDriversData(response.data.drivers || []);
    } catch (error) {
      console.error('Error fetching payments data:', error);
      setError('Error al cargar los datos de pagos');
    } finally {
      setIsLoading(false);
    }
  }

  function handleRefresh() {
    fetchPaymentsData();
  }

  async function handleUpdatePayment(id, field, value, type) {
    try {
      // Special case: 'refresh' field means just refresh data
      if (field === 'refresh') {
        await fetchPaymentsData();
        return;
      }

      // Regular field update
      await axios.put(`${API_URL}/api/accounting/payments/${id}`, {
        admin_id: userId,
        field,
        value,
        type // 'user', 'provider', or 'driver'
      });
      
      // Refresh data after update
      await fetchPaymentsData();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Error al actualizar el pago');
    }
  }

  const subTabs = [
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'providers', label: 'Proveedores', icon: Package },
    { id: 'drivers', label: 'Conductores', icon: Truck }
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg font-montserrat font-semibold transition-all
                  ${activeSubTab === tab.id
                    ? 'bg-brand-primary text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all font-montserrat disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-slate-600">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="font-montserrat">Cargando datos...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Users Table */}
          {activeSubTab === 'users' && (
            <PaymentsTable
              data={usersData}
              type="user"
              onUpdate={handleUpdatePayment}
            />
          )}

          {/* Providers Table */}
          {activeSubTab === 'providers' && (
            <PaymentsTable
              data={providersData}
              type="provider"
              onUpdate={handleUpdatePayment}
            />
          )}

          {/* Drivers Table */}
          {activeSubTab === 'drivers' && (
            <PaymentsTable
              data={driversData}
              type="driver"
              onUpdate={handleUpdatePayment}
            />
          )}
        </>
      )}
    </div>
  );
}

export default PaymentsAccounting;